import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsAuthenticated, useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';

const SESSION_KEY = 'bloom-session-id';
const HEARTBEAT_INTERVAL = 60_000; // 1 minute
const BUFFER_FLUSH_INTERVAL = 10_000; // 10 seconds

interface TrackEvent {
  type: 'session_start' | 'session_end' | 'heartbeat' | 'page_view';
  sessionId: string;
  path?: string;
  referrer?: string;
}

function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Tracks user activity: sessions, page views, heartbeats.
 * Buffers events and sends them in batches to reduce network overhead.
 * Call this once in the app root (e.g., Layout or App).
 */
export function useActivityTracker() {
  const isAuthenticated = useIsAuthenticated();
  const user = useUser();
  const location = useLocation();
  const bufferRef = useRef<TrackEvent[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const prevPathRef = useRef<string | null>(null);

  const flush = useCallback(async () => {
    if (bufferRef.current.length === 0) return;
    const events = [...bufferRef.current];
    bufferRef.current = [];
    try {
      await apiClient.post('/activity/track/batch', { events });
    } catch {
      // Re-add failed events for retry (but cap to avoid memory leak)
      if (bufferRef.current.length < 50) {
        bufferRef.current.unshift(...events);
      }
    }
  }, []);

  const track = useCallback((event: TrackEvent) => {
    bufferRef.current.push(event);
    // Flush immediately for session lifecycle events
    if (event.type === 'session_start' || event.type === 'session_end') {
      flush();
    }
  }, [flush]);

  // Session start/end
  useEffect(() => {
    if (!isAuthenticated || !user) {
      // End session if logging out
      if (sessionIdRef.current) {
        track({ type: 'session_end', sessionId: sessionIdRef.current });
        sessionStorage.removeItem(SESSION_KEY);
        sessionIdRef.current = null;
      }
      return;
    }

    // Start session if we don't have one
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = generateSessionId();
      sessionStorage.setItem(SESSION_KEY, sid);
      track({ type: 'session_start', sessionId: sid });
    }
    sessionIdRef.current = sid;

    // Heartbeat
    heartbeatTimerRef.current = setInterval(() => {
      if (sessionIdRef.current) {
        track({ type: 'heartbeat', sessionId: sessionIdRef.current });
      }
    }, HEARTBEAT_INTERVAL);

    // Buffer flush
    flushTimerRef.current = setInterval(flush, BUFFER_FLUSH_INTERVAL);

    // End session on tab close
    const handleBeforeUnload = () => {
      if (sessionIdRef.current) {
        // Use sendBeacon for reliable delivery on page unload
        const payload = JSON.stringify({
          events: [{ type: 'session_end', sessionId: sessionIdRef.current }],
        });
        const token = localStorage.getItem('bloom_token');
        // sendBeacon doesn't support auth headers, fall back to fetch keepalive
        const baseUrl = import.meta.env.VITE_API_URL || '/api';
        fetch(`${baseUrl}/activity/track/batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      flush();
    };
  }, [isAuthenticated, user, track, flush]);

  // Page view tracking
  useEffect(() => {
    if (!sessionIdRef.current || !isAuthenticated) return;
    const path = location.pathname;
    if (path === prevPathRef.current) return; // Skip duplicate
    const referrer = prevPathRef.current || undefined;
    prevPathRef.current = path;

    track({
      type: 'page_view',
      sessionId: sessionIdRef.current,
      path,
      referrer,
    });
  }, [location.pathname, isAuthenticated, track]);
}
